import styled from "styled-components";
import { ReactNode, ReactElement } from "react";
import { AllIconName, Icon } from "../Icon";
import { TextButton } from "../Button";

import { primaryActive } from "../../typography/alpha";

export enum CardTestIds {
  Component = "card",
  CardActionButton = "card-action-button",
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-primary-200);
  border-radius: var(--size-border-radius-medium);
  background: var(--color-neutral-000);
  overflow: hidden;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-secondary-100);
  min-height: 42px;
  padding: 11px 16px;
  border-radius: inherit;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  ${primaryActive.subtitle3};
`;

const InfoText = styled.div`
  margin-top: 5px;
  ${primaryActive.caption};
`;

const Content = styled.section`
  flex: 1;
  overflow-y: auto;
  padding: 11px 11px 11px 16px;
  margin: 5px 5px 5px 0;
`;

type CardProps = {
  id?: string;
  className?: string;
  testId?: string;
  actionButtonTestId?: string;
  title: string | ReactElement;
  iconName?: AllIconName;
  children?: ReactNode;
  actionButtonLabel?: string;
  onClickActionButton?: () => void;
  disableActionButton?: boolean;
  infoText?: string;
};

export const Card = ({
  id,
  className,
  testId,
  actionButtonTestId,
  title,
  iconName,
  children,
  actionButtonLabel,
  onClickActionButton,
  disableActionButton = false,
  infoText,
}: CardProps) => (
  <Container
    id={id}
    className={className}
    data-testid={testId ?? CardTestIds.Component}
  >
    <Header>
      <div>
        <Title>
          {iconName && <Icon iconName={iconName} iconSize="small" plain />}
          {title}
        </Title>
        {infoText && <InfoText>{infoText}</InfoText>}
      </div>
      {actionButtonLabel && onClickActionButton && (
        <TextButton
          data-testid={actionButtonTestId ?? CardTestIds.CardActionButton}
          onClick={onClickActionButton}
          disabled={disableActionButton}
        >
          {actionButtonLabel}
        </TextButton>
      )}
    </Header>
    <Content>{children}</Content>
  </Container>
);
